export const MAX_UPLOAD_FILE_SIZE_BYTES = 2 * 1024 * 1024;
export const MAX_UPLOAD_FILE_SIZE_LABEL = "2 MB";
export const MAX_DOCUMENT_UPLOAD_FILE_SIZE_BYTES = 70 * 1024 * 1024;
export const MAX_DOCUMENT_UPLOAD_FILE_SIZE_LABEL = "70 MB";

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const OFFICE_XML_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);
const LEGACY_OFFICE_TYPES = new Set([
  "application/msword",
  "application/vnd.ms-excel",
]);

function emitStatus(onStatusChange, payload) {
  if (typeof onStatusChange === "function") {
    onStatusChange(payload);
  }
}

function yieldToBrowser() {
  return new Promise((resolve) => {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function fileTypeLabel(fileKindLabel, count) {
  return count === 1 ? fileKindLabel : `${fileKindLabel}s`;
}

function sizeLimitLabel(maxBytes) {
  if (maxBytes === MAX_UPLOAD_FILE_SIZE_BYTES) return MAX_UPLOAD_FILE_SIZE_LABEL;
  if (maxBytes === MAX_DOCUMENT_UPLOAD_FILE_SIZE_BYTES) return MAX_DOCUMENT_UPLOAD_FILE_SIZE_LABEL;

  const sizeInMb = maxBytes / (1024 * 1024);
  return Number.isInteger(sizeInMb) ? `${sizeInMb} MB` : `${sizeInMb.toFixed(1)} MB`;
}

function buildSkipMessage(files, fileKindLabel, maxBytesLabel) {
  if (!files.length) return "";
  return `${files.length} ${fileTypeLabel(fileKindLabel, files.length)} were skipped because they are above ${maxBytesLabel}.`;
}

function buildCompressionConfirmationSkipMessage(files, fileKindLabel, maxBytesLabel) {
  if (!files.length) return "";
  return `${files.length} ${fileTypeLabel(fileKindLabel, files.length)} were skipped because they need compression below ${maxBytesLabel}.`;
}

function buildUnsupportedTypeMessage(skippedCount, fileKindLabel, allowedTypeLabel) {
  if (!skippedCount) return "";
  return `Only ${allowedTypeLabel} ${fileTypeLabel(fileKindLabel, skippedCount)} are allowed. Invalid files were skipped.`;
}

function buildCompressionFailureMessage(failures, maxBytesLabel) {
  if (!failures.length) return "";
  return [
    `Some files could not be reduced below the ${maxBytesLabel} upload limit:`,
    ...failures.map((item) => `- ${item.name}: ${item.reason}`),
  ].join("\n");
}

function replaceFileExtension(name, nextExtension) {
  const base = String(name || "upload").replace(/\.[^./\\]+$/, "");
  return `${base}.${nextExtension}`;
}

function extensionForMimeType(mimeType) {
  switch (mimeType) {
    case "image/jpeg":
      return "jpg";
    case "image/png":
      return "png";
    case "image/webp":
      return "webp";
    case "application/pdf":
      return "pdf";
    default:
      return "bin";
  }
}

function blobToFile(blob, originalFile, mimeType = blob.type) {
  const extension = extensionForMimeType(mimeType);
  const nextName = replaceFileExtension(originalFile?.name, extension);
  return new File([blob], nextName, {
    type: mimeType,
    lastModified: originalFile?.lastModified || Date.now(),
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Browser could not generate the compressed file."));
      },
      type,
      quality
    );
  });
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Image could not be loaded for compression."));
    };
    image.src = objectUrl;
  });
}

async function compressImageFile(file, maxBytes, maxBytesLabel) {
  const image = await loadImageFromFile(file);
  const scales = [1, 0.92, 0.84, 0.76, 0.68, 0.6, 0.52, 0.44, 0.36];
  const preferredTypes =
    file.type === "image/png"
      ? ["image/png", "image/jpeg", "image/webp"]
      : [file.type, "image/jpeg", "image/webp"];

  for (const mimeType of preferredTypes) {
    const qualities =
      mimeType === "image/png" ? [undefined] : [0.9, 0.82, 0.74, 0.66, 0.58, 0.5, 0.42, 0.34];

    for (const scale of scales) {
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Image compression is not supported in this browser.");
      }

      if (mimeType !== "image/png") {
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, width, height);
      }

      context.drawImage(image, 0, 0, width, height);

      for (const quality of qualities) {
        const blob = await canvasToBlob(canvas, mimeType, quality);
        if (blob.size <= maxBytes) {
          return blobToFile(blob, file, mimeType);
        }
      }
    }
  }

  throw new Error(`Could not compress this image below ${maxBytesLabel}.`);
}

async function compressPdfFile(file, maxBytes, maxBytesLabel) {
  const { PDFDocument } = await import("pdf-lib");
  const bytes = await file.arrayBuffer();
  const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const compressed = await pdf.save({
    useObjectStreams: true,
    addDefaultPage: false,
    updateFieldAppearances: false,
    objectsPerTick: 50,
  });
  const blob = new Blob([compressed], { type: "application/pdf" });
  if (blob.size <= maxBytes) {
    return blobToFile(blob, file, "application/pdf");
  }
  throw new Error(
    `Could not compress this PDF below ${maxBytesLabel}. Browser-side PDF compression cannot downscale embedded page images, so scanned PDFs often need to be resaved or compressed outside the browser.`
  );
}

async function compressOfficeXmlFile(file, maxBytes, maxBytesLabel) {
  const { default: JSZip } = await import("jszip");
  const zip = await JSZip.loadAsync(file);
  const blob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 9 },
  });

  if (blob.size <= maxBytes) {
    return blobToFile(blob, file, file.type);
  }

  throw new Error(
    `Could not compress this DOCX/XLSX below ${maxBytesLabel}. If it contains large images, resave/export a lighter file.`
  );
}

async function compressTextFile(file, maxBytes, maxBytesLabel) {
  const raw = await file.text();
  const normalized = raw
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n");
  const blob = new Blob([normalized], { type: "text/plain;charset=utf-8" });

  if (blob.size <= maxBytes) {
    return blobToFile(blob, file, "text/plain");
  }

  throw new Error(`Could not compress this TXT below ${maxBytesLabel}.`);
}

async function compressFileToLimit(file, maxBytes, maxBytesLabel) {
  if (IMAGE_TYPES.has(file.type)) {
    return compressImageFile(file, maxBytes, maxBytesLabel);
  }

  if (file.type === "application/pdf") {
    return compressPdfFile(file, maxBytes, maxBytesLabel);
  }

  if (OFFICE_XML_TYPES.has(file.type)) {
    return compressOfficeXmlFile(file, maxBytes, maxBytesLabel);
  }

  if (file.type === "text/plain") {
    return compressTextFile(file, maxBytes, maxBytesLabel);
  }

  if (LEGACY_OFFICE_TYPES.has(file.type)) {
    throw new Error(
      "Legacy DOC/XLS files cannot be safely recompressed in browser. Save them as DOCX/XLSX or PDF and try again."
    );
  }

  throw new Error("Automatic compression is not available for this file type.");
}

export async function prepareFilesForUpload(
  inputFiles,
  {
    allowedTypes = [],
    allowedTypeLabel = "supported",
    fileKindLabel = "file",
    maxBytes = MAX_UPLOAD_FILE_SIZE_BYTES,
    autoCompressOversized = false,
    confirmCompressionAboveBytes = null,
    confirmCompressionPrompt = "",
    onStatusChange = null,
  } = {}
) {
  const files = Array.isArray(inputFiles) ? inputFiles.filter(Boolean) : [];
  if (!files.length) {
    return [];
  }
  const maxBytesLabel = sizeLimitLabel(maxBytes);
  const compressionThresholdLabel = confirmCompressionAboveBytes
    ? sizeLimitLabel(confirmCompressionAboveBytes)
    : "";

  emitStatus(onStatusChange, {
    active: true,
    stage: "preparing",
    message: `Checking selected ${fileTypeLabel(fileKindLabel, files.length)}...`,
  });
  await yieldToBrowser();

  try {
    const validTypeFiles = files.filter((file) => allowedTypes.includes(file.type));
    const invalidTypeCount = files.length - validTypeFiles.length;
    const oversizedFiles = validTypeFiles.filter((file) => file.size > maxBytes);
    const compressionPromptFiles = confirmCompressionAboveBytes
      ? validTypeFiles.filter(
        (file) => file.size > confirmCompressionAboveBytes && file.size <= maxBytes
      )
      : [];
    const shouldCompressOversized =
      oversizedFiles.length > 0 &&
      (
        autoCompressOversized ||
        window.confirm(
          `${oversizedFiles.length} ${fileTypeLabel(fileKindLabel, oversizedFiles.length)} exceed the ${maxBytesLabel} upload limit. Compress and upload ${oversizedFiles.length === 1 ? "it" : "them"}?`
        )
      );
    const shouldContinueWithServerCompression =
      compressionPromptFiles.length === 0 ||
      window.confirm(
        confirmCompressionPrompt ||
        `${compressionPromptFiles.length} ${fileTypeLabel(fileKindLabel, compressionPromptFiles.length)} exceed ${compressionThresholdLabel} and will be compressed during upload. Continue?`
      );

    const preparedFiles = [];
    const compressionFailures = [];

    for (const [index, file] of validTypeFiles.entries()) {
      if (file.size <= maxBytes) {
        if (
          confirmCompressionAboveBytes &&
          file.size > confirmCompressionAboveBytes &&
          !shouldContinueWithServerCompression
        ) {
          continue;
        }
        preparedFiles.push(file);
        continue;
      }

      if (!shouldCompressOversized) {
        continue;
      }

      emitStatus(onStatusChange, {
        active: true,
        stage: "compressing",
        message: `Compressing ${fileKindLabel} ${index + 1} of ${validTypeFiles.length}: ${file.name}`,
      });
      await yieldToBrowser();

      try {
        preparedFiles.push(await compressFileToLimit(file, maxBytes, maxBytesLabel));
      } catch (error) {
        compressionFailures.push({
          name: file.name || "Unnamed file",
          reason: error?.message || "Compression failed.",
        });
      }
    }

    const messages = [
      buildUnsupportedTypeMessage(invalidTypeCount, fileKindLabel, allowedTypeLabel),
      oversizedFiles.length && !shouldCompressOversized
        ? buildSkipMessage(oversizedFiles, fileKindLabel, maxBytesLabel)
        : "",
      compressionPromptFiles.length && !shouldContinueWithServerCompression
        ? buildCompressionConfirmationSkipMessage(
          compressionPromptFiles,
          fileKindLabel,
          compressionThresholdLabel
        )
        : "",
      buildCompressionFailureMessage(compressionFailures, maxBytesLabel),
    ].filter(Boolean);

    if (messages.length) {
      window.alert(messages.join("\n\n"));
    }

    return preparedFiles;
  } finally {
    emitStatus(onStatusChange, {
      active: false,
      stage: "idle",
      message: "",
    });
  }
}
